import { ArrowDownLeft, ArrowUpRight, ChevronRight } from "lucide-react";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { formatRail } from "@/shared/utils/railDisplay";

import type { ActivityRow as ActivityRowData } from "../types/paymentsActivity.types";
import { ActivityStatusPill } from "./ActivityStatusPill";

export interface ActivityRowProps {
  row: ActivityRowData;
  onClick: (row: ActivityRowData) => void;
}

/**
 * One runtime operation as a list row.
 *
 * The amount is the NET money movement, signed by direction (payins credit the wallet, payouts leave it), and
 * rendered from the decimal string — never through `Number`, which silently drops the last of the 8 fraction
 * digits. `provider_reference` shows as an opaque reference and is never labeled with the provider's identity
 * (rule 12); copying it lives in the drawer, because a nested button inside a row button is not operable.
 */
export function ActivityRow({ row, onClick }: ActivityRowProps) {
  const { activeLanguage } = useTranslations("paymentsActivity");
  const inbound = row.direction === "payins";

  return (
    <button
      type="button"
      onClick={() => onClick(row)}
      className="grid w-full grid-cols-[auto_1fr_auto] items-center gap-3.5 border-b border-border bg-background px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-muted/40 focus:bg-muted/40 focus:outline-none sm:grid-cols-[auto_1fr_auto_auto]"
    >
      <div
        aria-hidden="true"
        className={cn(
          "grid h-8 w-8 shrink-0 place-items-center rounded-md border",
          inbound
            ? "border-primary/20 bg-primary/10 text-primary"
            : "border-border bg-muted text-foreground/70",
        )}
      >
        {inbound ? <ArrowDownLeft size={14} /> : <ArrowUpRight size={14} />}
      </div>

      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <ActivityStatusPill row={row} />
          <span className="truncate text-[13px] font-medium text-foreground">
            {row.concept ?? row.providerReference ?? "—"}
          </span>
        </div>
        <div className="mt-0.5 truncate text-[12px] text-muted-foreground">
          <span className="font-mono tracking-wide">{formatDateTime(row.createdAt)}</span>
          {" · "}
          {row.country} · {formatRail(row.rail)}
        </div>
      </div>

      <div className="hidden shrink-0 sm:block">
        <ColocaBadge variant="secondary" className="text-[10px]">
          {row.currency}
        </ColocaBadge>
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        <div className="text-right">
          <span
            className={cn(
              "block whitespace-nowrap font-mono text-[14px] font-medium tabular-nums",
              inbound ? "text-primary" : "text-foreground",
            )}
          >
            {inbound ? "+" : "−"}
            <ColocaAmountDisplay
              value={row.amount}
              locale={activeLanguage}
              minimumFractionDigits={2}
              maximumFractionDigits={8}
              size="sm"
              className="text-[14px]"
            />
          </span>
          <span className="mt-0.5 block font-mono text-[10.5px] uppercase tracking-wider text-muted-foreground sm:hidden">
            {row.currency}
          </span>
        </div>
        <ChevronRight size={14} className="text-muted-foreground" aria-hidden="true" />
      </div>
    </button>
  );
}
